// 098 -- Netball scoring rules + helpers.
// Deliberately kept separate from rugby-scoring.ts -- structural similarities
// are fine as duplication. Prefer parallel sport-specific modules over a
// shared abstraction (see feedback_sport_scoring_no_abstraction.md).

export const NETBALL_SCORING = {
  // Netball point values per event type.
  points: {
    goal: 1,
    missed_goal: 0,
    intercept: 0,
    yellow_card: 0,
    red_card: 0,
    correction: 0, // signed delta captured on the event row
  } as const,
  // 4 × 15 min quarters with a short break. Overrides via
  // Match.netball_quarter_length_mins.
  period_structure: {
    count: 4,
    default_length_mins: 15,
    break_length_mins: 4,
    labels: ["Q1", "Q2", "Q3", "Q4"] as const,
  },
  // SA school convention -- 3 / 1 / 0. No try-bonus or losing-bonus concept.
  league_rules: {
    win_points: 3,
    draw_points: 1,
    loss_points: 0,
  },
} as const;

export const NETBALL_SCORE_EVENT_TYPES = [
  "goal",
  "missed_goal",
  "intercept",
  "yellow_card",
  "red_card",
  "correction",
] as const;
export type NetballScoreEventType = (typeof NETBALL_SCORE_EVENT_TYPES)[number];

export const NETBALL_NON_SCORING_EVENTS = new Set<NetballScoreEventType>([
  "missed_goal",
  "intercept",
  "yellow_card",
  "red_card",
  "correction",
]);

export interface NetballQuarterBreakdown {
  q1: number;
  q2: number;
  q3: number;
  q4: number;
}

export interface NetballTeamAggregate {
  score: number;
  breakdown: NetballQuarterBreakdown;
}

/** Add an event's score impact to the aggregate for one side. Pure. */
export function applyNetballDelta(
  agg: NetballTeamAggregate,
  event: { event_type: NetballScoreEventType; quarter: number; points: number },
): NetballTeamAggregate {
  // correction's points arrive signed; everything else matches the rule table.
  const delta = event.event_type === "correction"
    ? event.points
    : NETBALL_SCORING.points[event.event_type] ?? 0;
  if (delta === 0) return agg;
  const q = Math.min(Math.max(event.quarter, 1), 4) as 1 | 2 | 3 | 4;
  const key = `q${q}` as keyof NetballQuarterBreakdown;
  return {
    score: agg.score + delta,
    breakdown: { ...agg.breakdown, [key]: agg.breakdown[key] + delta },
  };
}

/** Contribution of a finalised match to one team's league-table row. */
export function accumulateNetballMatch(result: {
  home_team_id: string | null;
  away_team_id: string | null;
  home_score: number;
  away_score: number;
  final: boolean;
}, teamId: string): { played: number; won: number; drawn: number; lost: number; goals_for: number; goals_against: number; league_points: number } {
  if (!result.final) {
    return { played: 0, won: 0, drawn: 0, lost: 0, goals_for: 0, goals_against: 0, league_points: 0 };
  }
  const isHome = result.home_team_id === teamId;
  const isAway = result.away_team_id === teamId;
  if (!isHome && !isAway) {
    return { played: 0, won: 0, drawn: 0, lost: 0, goals_for: 0, goals_against: 0, league_points: 0 };
  }
  const gf = isHome ? result.home_score : result.away_score;
  const ga = isHome ? result.away_score : result.home_score;
  const won = gf > ga ? 1 : 0;
  const drawn = gf === ga ? 1 : 0;
  const lost = gf < ga ? 1 : 0;
  const league_points =
    won * NETBALL_SCORING.league_rules.win_points +
    drawn * NETBALL_SCORING.league_rules.draw_points +
    lost * NETBALL_SCORING.league_rules.loss_points;
  return {
    played: 1,
    won,
    drawn,
    lost,
    goals_for: gf,
    goals_against: ga,
    league_points,
  };
}

/** Empty aggregate -- useful as a starting state for reducers. */
export function emptyNetballAggregate(): NetballTeamAggregate {
  return { score: 0, breakdown: { q1: 0, q2: 0, q3: 0, q4: 0 } };
}
